import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import CustomTable from "../../../../components/custom-table/custom-table";
import ImageviewerModal from "../../../../components/modals/imageViewerModal/imageviewerModal";
import ImageViewerWithBadgesModal from "../../../../components/modals/imageViewerWithBadgesModal/imageViewerWithBadgesModal";
import {
  FilterSection,
  FilterValues,
} from "../../../../data-model/filterValues";
import { ScreenNamesForBigQuery } from "../../../../enums/eventsData";
import { ModalId } from "../../../../enums/modalId";
import { TableNames } from "../../../../enums/tableNames";
import { setFilter } from "../../../../redux/slice/filterValuesSlice";
import { closeModal as closeReduxModal } from "../../../../redux/slice/modalSlice";
import { AppDispatch, RootState } from "../../../../redux/store/store";

import { useRef } from "react";
import DocumentScreenshotViewerModal from "../../../../components/modals/documentScreenshotViewerModal/documentScreenshotViewerModal";
import ViewReviewModal from "../../../../components/modals/ViewReviewModal/ViewReviewModal";
import { DOCUMENT_SCREENSHOT_VIEWER_TITLE } from "../../../../constants/stringLiterals";
import { useExportTableData } from "../../../../hooks/useExportTableData";
import { useReviewIconClick } from "../../../../hooks/useReviewIconClick";
import { handleDocUploadScreenshotViewForOrdersForNewModalDesign } from "../../../../utils/handleDocUploadScreenshot";
import { handlePropagateEventOnClickInRpaStatusCell } from "../../../../utils/medicalPaOrdersTableHandlers";
import { refreshPostgresFacetCounts } from "../../../../utils/refreshPostgresFacetCounts";
import { useFilterOptionsGeneratorFromAlgolia } from "../../hooks/useFilterOptionsGeneratorFromAlgolia";
import { useModalOpenerForMyPaOrders } from "../../myPaOrders/hooks/useModalOpenerForMyPaOrders";
import { useFilterAndSearch } from "../hooks/useFilterAndSearch";
import { useInitialDataFetchingForTable } from "../hooks/useInitialDataFetchingForTable";
import { useNextPageDataFetch } from "../hooks/useNextPageDataFetch";
import { useReviewPendingOrdersTableData } from "../hooks/useReviewPendingOrdersTableData";
import { FILTER_CREATION_CONFIG_FOR_REVIEW_PENDING_ORDERS } from "../utils/filterConfigForReviewPendingOrders";
import { ReviewPendingOrdersTableColumns } from "../utils/generateTableDataFromAlgoliaRecords";

/**
 * Displays the "Review Pending Orders" worklist table backed by Postgres.
 * Supports filtering by auth status segment, search, infinite-scroll pagination,
 * and opens modals for RPA screenshots, image viewers, document viewers, and review details.
 *
 * @param props.authStatus - Optional auth status segment to filter orders by (e.g. "no_auth_required")
 */
const ReviewPendingOrdersTableFromAlgolia = (props: {
  authStatus?: string;
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const abortController = useRef<AbortController | null>(null);
  const { data: authStatusOptions } = useSelector(
    (state: RootState) => state.authStatusOptions,
  );
  const handleReviewIconClick = useReviewIconClick();

  const navigate = useNavigate();
  const {
    reviewPendingOrdersState,
    setReviewPendingOrdersState,
    tableData,
    setTableData,
  } = useReviewPendingOrdersTableData();

  const authStatusProp = props?.authStatus;

  const tableName = authStatusProp
    ? `${TableNames.REVIEW_PENDING_ORDERS_FROM_ALGOLIA}__${authStatusProp}`
    : TableNames.REVIEW_PENDING_ORDERS_FROM_ALGOLIA;

  const { initialDataFetchingExecuted, setInitialDataFetchingExecuted } =
    useInitialDataFetchingForTable(
      setReviewPendingOrdersState,
      setTableData,
      tableName,
      authStatusProp,
    );

  const {
    paginationResetCount,
    setSearchTerm,
    searchTerm,
    validFilters,
    filterPayload,
  } = useFilterAndSearch(
    initialDataFetchingExecuted,
    setTableData,
    setReviewPendingOrdersState,
    authStatusProp,
  );

  const { exportData, loading: exportingInProgress } = useExportTableData();

  const { setEndIndex } = useNextPageDataFetch(
    validFilters,
    initialDataFetchingExecuted,
    searchTerm ?? "",
    reviewPendingOrdersState,
    setReviewPendingOrdersState,
    setTableData,
    tableName,
    authStatusProp,
  );

  const { openedModalId, metaData } = useModalOpenerForMyPaOrders();

  const { filterData, isPostgresFacetResultsLoading } =
    useFilterOptionsGeneratorFromAlgolia(
      FILTER_CREATION_CONFIG_FOR_REVIEW_PENDING_ORDERS,
    );

  const handleRefreshButtonClick = () => {
    setInitialDataFetchingExecuted(false);
    refreshPostgresFacetCounts(dispatch);
  };

  return (
    <>
      <div className="h-full">
        <CustomTable
          tableHeaders={ReviewPendingOrdersTableColumns}
          tableData={tableData}
          tableName={
            authStatusProp
              ? `${TableNames.REVIEW_PENDING_ORDERS_FROM_ALGOLIA}__${authStatusProp}`
              : TableNames.REVIEW_PENDING_ORDERS_FROM_ALGOLIA
          }
          itemsPerPage={10}
          pagesPerView={4}
          count={tableData?.length ?? 0}
          totalCount={reviewPendingOrdersState?.data?.total_count ?? 0}
          endIndexOfTable={setEndIndex}
          searchingText={(searchText: string) => {
            setSearchTerm(searchText);
          }}
          hideSearchBar={false}
          showInLineLoader={reviewPendingOrdersState.showinLineLoader}
          shouldLogEvent={true}
          screenName={ScreenNamesForBigQuery.WORKLIST}
          isFetching={reviewPendingOrdersState.loading}
          requestForPaginationReset={paginationResetCount}
          filterSectionsData={filterData as FilterSection[]}
          showFilterButton={
            !reviewPendingOrdersState.loading && !isPostgresFacetResultsLoading
          }
          onFilterApplyClicked={(data: FilterValues) => {
            dispatch(setFilter(data));
          }}
          onRefreshButton={handleRefreshButtonClick}
          showRefreshButton={true}
          isDisabled={
            reviewPendingOrdersState.loading ||
            reviewPendingOrdersState.showinLineLoader
          }
          onRowClick={(rowData: any) => {
            navigate(`/worklists/evbv/${rowData["id"]}`);
          }}
          onPropagateEventOnClickInRpaStatusCell={(id: string) => {
            handlePropagateEventOnClickInRpaStatusCell(
              id,
              dispatch,
              authStatusOptions,
              abortController,
            );
          }}
          onPropagateEventOnClickInRpaStatusTwoCell={(id: string) => {
            handleDocUploadScreenshotViewForOrdersForNewModalDesign(
              id,
              dispatch,
              abortController,
            );
          }}
          onOverlappingIconClick={(icon_id: string, order_id: string) =>
            handleReviewIconClick(icon_id, order_id, abortController)
          }
          showExportButton={true}
          onExportButtonClick={() => {
            if (!filterPayload) return;
            exportData(
              { ...filterPayload, export: true },
              "review-pending-orders-export.csv",
            );
          }}
          exportingInProgress={exportingInProgress}
          shouldDisableUiSorting={true}
        />
      </div>
      {openedModalId === ModalId.IMAGE_VIEWER_WITH_BADGES_MODAL && (
        <ImageViewerWithBadgesModal
          tabs={metaData?.tabs ?? []}
          showLoading={metaData?.showLoading ?? false}
          title="Write Back Trail"
        />
      )}
      {openedModalId === ModalId.IMAGE_VIEWER_MODAL && (
        <ImageviewerModal
          fileUrls={metaData?.fileUrls ?? []}
          filePaths={metaData?.filePaths ?? []}
          onClose={() => {
            dispatch(closeReduxModal());
            abortController.current?.abort();
            abortController.current = null;
          }}
          showLoader={metaData?.showLoader ?? false}
        />
      )}
      {openedModalId === ModalId.VIEW_REVIEW_MODAL && (
        <ViewReviewModal
          onClose={() => {
            dispatch(closeReduxModal());
            abortController.current?.abort();
            abortController.current = null;
          }}
          reviewData={metaData?.reviewData ?? null}
          isLoading={metaData?.isLoading ?? false}
        />
      )}
      {openedModalId === ModalId.DOCUMENT_SCREENSHOT_VIEWER_MODAL && (
        <DocumentScreenshotViewerModal
          title={DOCUMENT_SCREENSHOT_VIEWER_TITLE}
          onClose={() => {
            abortController.current?.abort();
            abortController.current = null;
          }}
        />
      )}
    </>
  );
};

export default ReviewPendingOrdersTableFromAlgolia;
